// =====================================================================
// Chamados — regras da FILA no FRONT (tempo parado e de quem se espera).
//
// ⚠️⚠️ ESPELHO de `server/chamados/chamados-fluxo.js`. A mesma regra vive nos
// dois lados porque o front (CRA/ESM) não importa do `server/` (CommonJS) — é a
// mesma situação já registrada no CLAUDE.md para a fila da Certificação e para
// os pesos dos formulários de qualidade.
// **Mudou aqui, muda lá — e vice-versa.** Há teste de espelho
// (`server/tests/espelhos-back-front.test.js`) comparando o COMPORTAMENTO das
// duas versões sobre as mesmas entradas, nunca o texto do código.
//
// ⚠️ DIVERGIR NÃO DÁ ERRO EM LUGAR NENHUM: a tela mostraria "parado há 3 dias" e
// a coluna do backend contaria 12, para o mesmo chamado. Ninguém liga o sintoma
// à causa.
//
// ⚠️ Arquivo AUTOCONTIDO de propósito (não importa nada): é o que permite ao
// carregador do teste de espelho convertê-lo para CommonJS com segurança.
// =====================================================================

/** Dia UTC de uma data — para contar dias corridos sem tropeçar em fuso. */
function diaUtc(d) {
    return Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Dias corridos entre duas datas (>= 0). `null` se a origem não for válida. */
export function diasCorridos(desde, hoje = new Date()) {
    if (!desde) return null;
    const d = desde instanceof Date ? desde : new Date(desde);
    if (isNaN(d)) return null;
    return Math.max(0, Math.round((diaUtc(hoje) - diaUtc(d)) / 86400000));
}

/**
 * Data do último ATO HUMANO do chamado — o marco de "parado há".
 *
 * ⚠️ NUNCA `DATA_MODIFICACAO`: é `ON UPDATE CURRENT_TIMESTAMP` e qualquer
 * migração a rejuvenesce em bloco (medido na Certificação em 17/08/2026: um
 * relatório parado há 39 dias aparecia como 10).
 */
export function marcoUltimoAto(c) {
    if (!c) return null;
    const datas = [
        c.ABERTO_EM, c.TRIADO_EM, c.ATRIBUIDO_EM, c.INICIADO_EM,
        // ⚠️ `FINALIZADO_EM` e `VALIDADO_EM` ENTRARAM EM 31/08/2026, com os dois
        //    estados novos. Esquecer um deles não dá erro: o chamado só passa a
        //    contar "parado há" desde um ato ANTERIOR, e aparece velho no dia
        //    seguinte ao ato mais recente. Ato novo com data própria entra AQUI.
        c.FINALIZADO_EM, c.CONCLUIDO_EM, c.ENCERRADO_EM, c.VALIDADO_EM, c.CANCELADO_EM,
    ].filter(Boolean).map((d) => new Date(d)).filter((d) => !isNaN(d));
    return datas.length ? new Date(Math.max(...datas)) : null;
}

/**
 * O trabalho neste chamado ACABOU? (não se corrige, não se agenda, não se cancela)
 *
 * ⚠️⚠️ NASCEU EM 31/08/2026 PORQUE A LISTA `['Encerrado', 'Cancelado']` ESTAVA
 *    ESCRITA EM CINCO LUGARES — três na tela, duas no servidor — e o `Validado`
 *    teria de entrar em todas. Esquecer UMA não daria erro: deixaria corrigir,
 *    agendar ou cancelar um chamado já fechado pela loja, apagando o registro de
 *    um serviço executado, fotografado e validado.
 *
 * ⚠️ `Encerrado` ENTRA aqui mas NÃO é status final: ele ainda anda (vira
 *    `Validado`). O que acabou é o TRABALHO — a esteira segue, esperando só a
 *    ciência da loja. São perguntas diferentes, e por isso esta função não é o
 *    `STATUS_FINAIS`.
 * ⚠️ `Finalizado` fica FORA: o atendimento parou esperando o Invgate ou uma peça,
 *    e ali ainda cabe corrigir o tipo, reagendar a visita ou desistir.
 */
export function trabalhoAcabou(status) {
    return ['Encerrado', 'Validado', 'Cancelado', 'Concluido'].includes(status);
}

/** Dias corridos desde o último ato humano; `null` sem marco algum. */
export function diasParado(c, hoje = new Date()) {
    return diasCorridos(marcoUltimoAto(c), hoje);
}

/**
 * Encurta um texto livre para caber na coluna, sem cortar no meio da palavra.
 *
 * ⚠️ Existe por causa do MOTIVO DA PAUSA, que é digitado pelo técnico e não tem
 *    tamanho previsível. O texto inteiro continua na ficha do chamado.
 */
function resumirTexto(texto, limite) {
    const t = String(texto || '').trim().replace(/\s+/g, ' ');
    if (t.length <= limite) return t;
    const cortado = t.slice(0, limite);
    const espaco = cortado.lastIndexOf(' ');
    return `${(espaco > limite * 0.6 ? cortado.slice(0, espaco) : cortado).trim()}…`;
}

/**
 * De quem o chamado está esperando algo, em texto para a coluna "Aguardando".
 * Responde pelo PAPEL, não pela pessoa. `null` quando não há espera.
 */
export function aguardandoDe(c) {
    if (!c) return null;
    switch (c.STATUS) {
        case 'Aberto': return 'Triagem do TI';
        case 'Atribuido': return `Técnico${c.TECNICO_NOME ? ` (${c.TECNICO_NOME})` : ''}`;
        case 'Em atendimento': return `Técnico${c.TECNICO_NOME ? ` (${c.TECNICO_NOME})` : ''}`;
        // ⚠️⚠️ A PAUSA MOSTRA O MOTIVO, não um dono (17/09/2026). Enquanto ela se
        //    chamava 'Aguardando loja' a resposta era a loja; hoje o técnico pausa
        //    também por falta de acesso ou espera curta, e dizer "Loja FER" nesses
        //    casos mandaria cobrar quem não deve nada. O motivo é obrigatório na
        //    pausa, então o fallback só alcança chamado pausado ANTES dessa data.
        case 'Atendimento Pausado':
        case 'Aguardando loja':
            return c.PAUSA_MOTIVO ? `Pausado: ${resumirTexto(c.PAUSA_MOTIVO, 40)}` : 'Pausado';
        // ⚠️⚠️ A ESPERA AQUI É DE UM TERCEIRO, e a coluna DIZ QUAL — é a razão de
        //    `Finalizado` não ter virado um segundo "pausado". Quem cobra o
        //    Invgate é o TI; quem cobra a peça é Compras. "Parado" sem dizer o quê
        //    manda a pessoa abrir o chamado para descobrir de quem cobrar.
        case 'Finalizado':
            return c.FINALIZADO_MOTIVO === 'Pecas' ? 'Compra de peças' : 'Invgate (Sonda)';
        // ⚠️⚠️ 'Encerrado' DEIXOU DE SER O FIM em 31/08: o serviço acabou e a loja
        //    ainda não deu ciência. Esta linha é o que faz "o que falta validar"
        //    existir como fila de trabalho — sem ela, a validação seria um botão
        //    que ninguém saberia que precisa apertar.
        // ⚠️ DEM-00009: se a loja NÃO validou e abriu um filho, o pai SAI da fila de
        //    validação por derivação — ele não aguarda mais ciência, aguarda o novo
        //    chamado. Sem isto ficaria cobrando "Validação da loja" para sempre, ao
        //    lado do filho que já herdou o problema. `REABERTO_EM` vem da lista/detalhe.
        case 'Encerrado':
            return c.REABERTO_EM
                ? `Reaberto no #${c.REABERTO_EM}`
                : `Validação da loja ${c.LOJA || ''}`.trim();
        // ⚠️ LEGADO: nada escreve mais 'Concluido' (a validação da loja saiu em
        //    21/08/2026). Uma linha antiga nesse estado espera o TI fechar, não mais
        //    a loja validar — antes esta linha dizia "Validação da loja X".
        case 'Concluido': return `Técnico${c.TECNICO_NOME ? ` (${c.TECNICO_NOME})` : ''}`;
        default: return null;                                 // Validado/Cancelado
    }
}
